import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';
import {connect, useSelector} from 'react-redux';

import {isFileConfigVariable} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {areAnyFeaturesEnabled, isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import CanvasContainer from 'benchling-apps/interactivity/canvases/CanvasContainer';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as Context from 'Context';
import {isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import getConfig from 'getConfig';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingAppFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ConnectionStatus,
  InstrumentServiceDetailsLoader,
  InstrumentServiceLoadStatus,
} from 'instruments/connections/ConnectionConstants';
import {getInstrumentServiceDetails} from 'instruments/connections/ConnectionDAO';
import RunAutomationFileViewer from 'lab-automation/automation-files/RunAutomationFileViewer';
import {
  FromServerRunOutputSamplesTable,
  RunOutputSamplesTable,
} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {FromServerResultsTable, ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {NOTE_PADDING} from 'notebook/notes/NoteConstants';
import {Review} from 'notebook/reviews/ReviewCriteriaConstants';
import notifications from 'notifications';
import {BenchlingDispatch, getStore} from 'redux-stores/Store';
import {
  GetAnalysisTemplateForAssayRun,
  GetAnalysisTemplateForAssayRunVariables,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {GetAssayRunAnalysisIds} from 'results/analysis-integration/__generated/GetAssayRunAnalysisIds';
import {
  GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY,
  GET_ASSAY_RUN_ANALYSIS_IDS,
  isVariableMulti,
} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRecordOriginType} from 'results/assays/AssayRecordConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import {
  integrationExpandedGlobalClientState,
  AnalysisTemplateIntegrationRunToolbar,
  AnalysisTemplateIntegrationRunAssayGroupBody,
  fileVariableBindingsGlobalClientState,
  isVariableRequired,
  runAnalysisAutomation,
} from 'runs/assay-group/AnalysisTemplateIntegrationRun';
import {EditRunToolbar, SubmitToolbar, validateRunForSubmit} from 'runs/assay-group/AssayGroupEditButtons';
import {updateTagsForAssayRun} from 'runs/assay-group/AssayGroupReduxActions';
import AssayResultsContainer, {AssayResultsContainerProps} from 'runs/assay-group/AssayResultsContainer';
import AssayRunFields from 'runs/assay-group/AssayRunFields';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CollapserChevron from 'ui-library/CollapserChevron';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {NLADVersionedAutomationFiles} from '../AttachmentConstants';
import AssayGroupContainer from './AssayGroupContainer';
import {runExpandedState} from './runExpandedState';
import {computeVisibleRunFields, getEditableRunTags} from './VisibleRunFields';

interface AssayGroupHeaderProps {
  displayAnalysisTemplateIntegrationUI?: boolean;
  editButtons?: React.ReactNode;
  titleText: string;
  originType?: string;
}

export function AssayGroupHeader(props: AssayGroupHeaderProps): JSX.Element {
  return (
    <div className="u-lr-split u-spacing-bottom">
      <div>
        <span
          className={
            props.displayAnalysisTemplateIntegrationUI
              ? classNames('resultsAttachment-assayRun-titleText', css(styles.analysisIntegrationUIHeader))
              : 'resultsAttachment-assayRun-titleText'
          }
          {...getTestProps({component: 'AssayGroupHeader', element: 'titleText'})}
        >
          {props.titleText}
        </span>
        {props.originType === AssayRecordOriginType.API && (
          <span className="resultsAttachment-assayRun-originType u-small-spacing-left">API</span>
        )}
      </div>
      <div className="resultsAttachment-assayRun-statusText">{props.editButtons}</div>
    </div>
  );
}

export interface AssayGroupProps extends AssayResultsContainerProps {
  assayRunSchemaInfo: {
    assayRunSchema: AssayRunSchema;
    versionedName: string | null;
    versionedFields: Array<SchemaField> | null;
  };
  assayRunInfo: {
    assayRunId: string | null;
    versionedTags: Array<Tag> | null;
    versionedConnectionId: string | null;
    versionedEquipmentId: string | null;
  };
  automationFiles: NLADVersionedAutomationFiles;
  versionedStructuredTables: Array<RunOutputSamplesTable | ResultsTable>;
  handleRefreshVersionedData: () => Promise<void>;
  handleUpdateTags: (tags: Array<Tag>, connectionId: string | null, equipmentId: string | null) => void;
  handleUpdateAutomationFiles: (
    files: Partial<NLADVersionedAutomationFiles>,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
  handleRemoveAssayRun: () => void;
  resultSchemaToCount: {[schemaId: string]: number};
  review: Review | null;
  refreshCounter?: number;
}

interface DispatchProps {
  onUpdateAssayRun: (
    assayRunId: string,
    cid: string,
    tags: Array<Tag>,
    connectionId: string | null,
    equipmentId: string | null,
    studyIds: Array<string>
  ) => Promise<void>;
}

// exported for tests
export function shouldDisableEditButton(
  hasEditableRunTags: boolean,
  hasConnectionSchema: boolean,
  hasEditableEquipment: boolean,
  generatedInputFiles: boolean,
  processedOutputFiles: boolean
): boolean {
  const isConnectionEditable =
    isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}) &&
    hasConnectionSchema &&
    !generatedInputFiles &&
    !processedOutputFiles;
  const isEquipmentEditable = hasEditableEquipment && !generatedInputFiles && !processedOutputFiles;
  const areFieldsEditable = hasEditableRunTags || isConnectionEditable || isEquipmentEditable;
  return (
    !areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}]) ||
    !areFieldsEditable ||
    BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_CREATION ||
    (generatedInputFiles && BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_INPUT_FILE_GENERATED) ||
    (processedOutputFiles && BenchlingConfig.LAB_AUTOMATION_LOCK_RUN_AFTER_OUTPUT_FILE_PROCESSED)
  );
}

interface AssayGroupBodyProps extends AssayGroupProps, DispatchProps {
  isExpanded: boolean;
  visibleRunFields: Array<SchemaField>;
  isEditing: boolean;
  connectionSchema: AssayRunSchema['connectionSchema'];
  connectionStatus: ConnectionStatus | null | undefined;
  generatedInputFiles: boolean;
  processedOutputFiles: boolean;
  locked: boolean;
  instrumentServiceDetails: InstrumentServiceDetailsLoader;
  isAutomationRun: boolean;
}

function AssayGroupBody(props: AssayGroupBodyProps): JSX.Element | null {
  return props.isExpanded ? (
    <>
      <AssayRunFields
        assayRun={props.assayRun}
        displayAnalysisTemplateIntegrationUI={false}
        liveFields={props.visibleRunFields}
        versionedFields={props.assayRunSchemaInfo.versionedFields}
        versionedTags={props.assayRunInfo.versionedTags}
        cid={props.cid}
        isEditable={props.isEditing}
        connectionSchemaId={props.connectionSchema?.id ?? null}
        versionedConnectionId={props.assayRunInfo.versionedConnectionId}
        versionedEquipmentId={props.assayRunInfo.versionedEquipmentId}
        connectionStatus={props.connectionStatus}
        hasAutomationFiles={props.generatedInputFiles || props.processedOutputFiles}
      />
      <CanvasContainer
        resourceId={props.assayRun.id}
        featureType={BenchlingAppFeatureType.ASSAY_RUN}
        enabledOverride={!props.locked}
      />
      <RunAutomationFileViewer
        runId={props.assayRun.id}
        automationFiles={props.automationFiles}
        locked={props.locked || props.isEditing}
        isConnectionRun={props.connectionSchema != null}
        connectionId={props.assayRun.connection?.id ?? null}
        instrumentServiceDetails={props.instrumentServiceDetails}
        handleRefreshVersionedData={props.handleRefreshVersionedData}
        handleUpdateAutomationFiles={props.handleUpdateAutomationFiles}
        versionedStructuredTables={props.versionedStructuredTables}
        getIdFromCid={props.editorProps.getIdFromCid}
        fileId={props.fileId}
        viewer={props.viewer}
        refreshCounter={props.refreshCounter}
      />
      {!props.isAutomationRun && (
        <AssayResultsContainer
          assayResults={props.assayResults}
          assayResultSchemas={props.assayResultSchemas}
          childrenFetching={props.childrenFetching}
          dataTables={props.dataTables}
          displayConfigBySchemaId={props.displayConfigBySchemaId}
          editor={props.editor}
          editorProps={props.editorProps}
          handleUpdateNoteLinkedAssayData={props.handleUpdateNoteLinkedAssayData}
          assayRun={props.assayRun}
          provenanceUuid={props.provenanceUuid}
          resultSchemaToCount={props.resultSchemaToCount}
          usedTableNames={props.usedTableNames}
          /* Proxied note props */
          cid={props.cid}
          fileId={props.fileId}
          indentation={props.indentation}
          index={props.index}
          locked={props.locked}
          viewer={props.viewer}
        />
      )}
    </>
  ) : null;
}

const AssayGroup: React.FunctionComponent<AssayGroupProps & DispatchProps> = (props) => {
  const [isEditing, setEditing] = useState(false);
  const [isExpanded, setIsExpanded] = runExpandedState.useGlobalClientState(props.cid);

  // State management for AnalysisTemplateIntegrationRun components
  const [integrationExpandedState, setIntegrationExpandedState] =
    integrationExpandedGlobalClientState.useGlobalClientState(props.cid);
  const [isRunningAutomation, setIsRunningAutomation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Determine whether we're in export/print preview mode. If we're exporting the entry, then we want to
  // expand the input/output sections of AnalysisTemplateIntegrationRun so that the final PDF shows all
  // run field values and names of created analyses.
  const {isExportView, isPrintPreviewView} = EditorPropertiesContext.useContext();
  useEffect(() => {
    if (isExportView || isPrintPreviewView) {
      setIntegrationExpandedState({
        inputIsExpanded: true,
        outputIsExpanded: true,
      });
    }
  }, [isExportView, isPrintPreviewView, setIntegrationExpandedState]);

  // Subscribe to the current tag values as they're updated by the user
  const currentTags = useSelector(
    (state: {ui: {schemaTags: {tagsByCid: {[key: string]: Array<Tag>}}}}) =>
      state.ui.schemaTags.tagsByCid[props.cid]
  );

  const analysisTemplateFromSchema = props.assayRunSchemaInfo.assayRunSchema.analysisTemplate;

  const hasAnalysisTemplateId =
    getConfig('ENABLE_DIRECT_RUN_TO_ANALYSIS_INTEGRATION') && analysisTemplateFromSchema?.id != null;

  const getAnalysisTemplateReponse = useSkippableBenchlingQuery<
    GetAnalysisTemplateForAssayRun,
    GetAnalysisTemplateForAssayRunVariables
  >(GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY, {
    variables: {analysisTemplateId: analysisTemplateFromSchema?.id || ''},
    skip: !hasAnalysisTemplateId,
  });

  const isAnalysisTemplateInaccessible =
    hasAnalysisTemplateId &&
    getAnalysisTemplateReponse &&
    getAnalysisTemplateReponse.type === QueryResultType.SUCCESS &&
    isInaccessible(getAnalysisTemplateReponse.data.analysisTemplate);

  const displayAnalysisTemplateIntegrationUI = hasAnalysisTemplateId && !isAnalysisTemplateInaccessible;
  const [fileVariableBindings, setFileVariableBindings] =
    fileVariableBindingsGlobalClientState.useGlobalClientState(props.cid);

  // Query the analysis template, then set the initial file variable bindings (we need the IDs of the config
  // variables in order to do this).
  let analysisTemplate: AnalysisTemplateNode | null = null;
  if (
    getAnalysisTemplateReponse &&
    getAnalysisTemplateReponse.type === QueryResultType.SUCCESS &&
    !isInaccessible(getAnalysisTemplateReponse.data.analysisTemplate)
  ) {
    analysisTemplate = getAnalysisTemplateReponse.data.analysisTemplate;
  }
  useEffect(() => {
    if (analysisTemplate != null && fileVariableBindings == null) {
      setFileVariableBindings(
        analysisTemplate.configVariables
          .filter((variable) => isFileConfigVariable(variable))
          .map(({id}) => ({variableId: id, variableValue: null}))
      );
    }
  }, [analysisTemplate, fileVariableBindings, setFileVariableBindings]);

  // Query for linked analyses, then automatically collapse the input section and expand the output section if
  // we know the run has generated outputs (i.e. analyses).
  const getAnalysisIdsResponse = useBenchlingQuery<GetAssayRunAnalysisIds>(GET_ASSAY_RUN_ANALYSIS_IDS, {
    variables: {runId: props.assayRun.apiId},
  });
  const analysisIds =
    getAnalysisIdsResponse.type === QueryResultType.SUCCESS &&
    !isInaccessible(getAnalysisIdsResponse.data.run)
      ? getAnalysisIdsResponse.data.run.analysisIds
      : null;
  useEffect(() => {
    // Ignore auto-collapse behavior if in export or print preview view, since we want all inputs/outputs to
    // be displayed in the entry's PDF
    if (isExportView || isPrintPreviewView) {
      return;
    }
    if (
      // In this case, we don't have information about the analyses to know whether to update the state. Even
      // if the query ultimately fails, it's ok to do nothing to override the default state (input expanded,
      // output collapsed).
      analysisIds == null ||
      // In this case, this hook or the user has already overridden the default output collapsed state, so we
      // should not overwrite the explicitly defined state.
      integrationExpandedState.outputIsExpanded != null
    ) {
      return;
    }
    if (analysisIds.length > 0) {
      setIntegrationExpandedState({
        inputIsExpanded: false,
        outputIsExpanded: true,
      });
    }
  }, [analysisIds, integrationExpandedState, setIntegrationExpandedState, isExportView, isPrintPreviewView]);

  const visibleRunFields = computeVisibleRunFields(
    props.assayRunSchemaInfo.assayRunSchema,
    props.displayConfigBySchemaId[props.assayRunSchemaInfo.assayRunSchema.id]
  );
  const locked =
    props.locked || !areAnyFeaturesEnabled([{feature: LabAutomationFeature.LAB_AUTOMATION_EDIT}]);
  const editableRunTags = getEditableRunTags(
    visibleRunFields,
    props.assayRun,
    props.assayRunInfo.versionedTags
  );
  const hasEditableRunTags = editableRunTags.length > 0;
  const generatedInputFiles =
    props.automationFiles.automationInputFiles != null &&
    props.automationFiles.automationInputFiles.filter(
      (aif) => aif.status === AutomationFileStatusGraphQL.succeeded
    ).length > 0;
  const processedOutputFiles =
    props.automationFiles.automationOutputFiles != null &&
    props.automationFiles.automationOutputFiles.filter(
      (aof) => aof.status === AutomationFileStatusGraphQL.succeeded
    ).length > 0;
  const connectionSchema = props.assayRunSchemaInfo.assayRunSchema.connectionSchema;
  const disableEditButton = shouldDisableEditButton(
    hasEditableRunTags,
    connectionSchema != null,
    props.assayRun.shouldRecordEquipment,
    generatedInputFiles,
    processedOutputFiles
  );
  const isAutomationRun =
    props.assayRunSchemaInfo.assayRunSchema.automationInputFileConfigs.length > 0 ||
    props.assayRunSchemaInfo.assayRunSchema.automationOutputFileConfigs.length > 0;

  const connectionId = props.assayRunInfo.versionedConnectionId ?? props.assayRun?.connection?.id ?? null;

  const [instrumentServiceDetails, setInstrumentServiceDetails] = useState<InstrumentServiceDetailsLoader>({
    status: InstrumentServiceLoadStatus.UNSET,
    data: null,
  });

  useEffect((): void => {
    if (connectionId) {
      setInstrumentServiceDetails({
        status: InstrumentServiceLoadStatus.LOADING,
        data: null,
      });
      getInstrumentServiceDetails(connectionId)
        .then((data) => {
          setInstrumentServiceDetails({data, status: InstrumentServiceLoadStatus.AVAILABLE});
        })
        .catch((error) => {
          notifications.notifyError(error);
          setInstrumentServiceDetails({data: null, status: InstrumentServiceLoadStatus.UNAVAILABLE});
        });
    }
  }, [connectionId]);
  const connectionStatus = instrumentServiceDetails.data?.status.value;

  const onSaveRunFields = async (): Promise<void> => {
    setIsSaving(true);
    const tags = getStore().getState().ui.schemaTags.tagsByCid[props.cid];
    const connectionId = getStore().getState().ui.schemaTags.connectionIdByCid[props.cid] ?? null;
    const equipmentId = getStore().getState().ui.schemaTags.equipmentIdByCid[props.cid] ?? null;
    const studyIds = getStore().getState().ui.schemaTags.studyIdsByCid[props.cid] ?? [];
    props.handleUpdateTags(tags, connectionId, equipmentId);
    if (!displayAnalysisTemplateIntegrationUI) {
      setEditing(false);
    }
    await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOrigin: ActionOrigin.NOTEBOOK_ENTRY_RUN,
        },
      },
      null,
      async () => {
        await props.onUpdateAssayRun(props.assayRun.id, props.cid, tags, connectionId, equipmentId, studyIds);
      }
    );
    setIsSaving(false);
  };

  const onRunAutomation = async (): Promise<void> => {
    setIsRunningAutomation(true);

    // Store the current analysis IDs before creating new one
    const previousAnalysisIds = analysisIds || [];

    await runAnalysisAutomation({
      runApiId: props.assayRun.apiId!,
      fileVariableBindings: fileVariableBindings!,
      previousAnalysisIds,
      onSaveRunFields,
      refetchAnalysisIds: () => getAnalysisIdsResponse.refetch(),
    });

    // Reset file variable bindings after automation completes
    setFileVariableBindings(fileVariableBindings!.map((binding) => ({...binding, variableValue: null})));

    // Automatically collapse the input section and expand the output section
    setIntegrationExpandedState({
      inputIsExpanded: false,
      outputIsExpanded: true,
    });
    setIsRunningAutomation(false);
  };

  let editButtons: React.ReactNode;
  if (displayAnalysisTemplateIntegrationUI) {
    // User can save if any run field values have changed since the schema was last saved
    const canSave =
      currentTags != null &&
      props.assayRun.allTags
        .filter((tag) => isSchemaTag(tag))
        .some((persistedTag: SchemaTag) => {
          const currentTag: SchemaTag | undefined = currentTags
            .filter((tag) => isSchemaTag(tag))
            .find((currentTag) => currentTag.tagSchemaField.apiId === persistedTag.tagSchemaField.apiId);
          return currentTag != null && currentTag.value !== persistedTag.rawValue;
        });
    editButtons = (
      <AnalysisTemplateIntegrationRunToolbar
        assayRun={props.assayRun}
        assayRunSchema={props.assayRunSchemaInfo.assayRunSchema}
        handleRemoveAssayRun={props.handleRemoveAssayRun}
        locked={locked}
        canRunAutomation={
          analysisTemplate != null &&
          fileVariableBindings != null &&
          // Check required file variable bindings are non-null and non-empty
          analysisTemplate.configVariables
            .filter((variable) => isFileConfigVariable(variable))
            .every((fileVariable) => {
              if (!isVariableRequired(fileVariable)) {
                return true;
              }
              return fileVariableBindings.some(
                ({variableId, variableValue}) =>
                  variableId === fileVariable.id &&
                  variableValue != null &&
                  // Either the variable is not isMulti, or the number of selected items is at least
                  // minItems
                  (!isVariableMulti(fileVariable.variableTypeConfigSpec) ||
                    variableValue.length >= (fileVariable.variableTypeConfigSpec.minItems ?? 0))
              );
            }) &&
          // Check that the required run fields are all non-null
          currentTags != null &&
          validateRunForSubmit(currentTags, null, null) == null
        }
        onRunAutomation={onRunAutomation}
        isRunning={isRunningAutomation}
        canSave={canSave}
        onSave={onSaveRunFields}
        isSaving={isSaving}
      />
    );
  } else {
    editButtons = isEditing ? (
      <SubmitToolbar
        onCancel={() => {
          setEditing(false);
        }}
        validateForSubmit={() =>
          validateRunForSubmit(
            getStore().getState().ui.schemaTags.tagsByCid[props.cid],
            getStore().getState().ui.schemaTags.connectionIdByCid[props.cid] ?? null,
            connectionSchema
          )
        }
        onSubmit={onSaveRunFields}
        submitText="Save"
      />
    ) : (
      <EditRunToolbar
        assayResults={props.assayResults}
        assayRun={props.assayRun}
        assayRunSchema={props.assayRunSchemaInfo.assayRunSchema}
        childrenFetching={props.childrenFetching}
        handleRefreshVersionedData={props.handleRefreshVersionedData}
        handleRemoveAssayRun={props.handleRemoveAssayRun}
        handleUpdateAutomationFiles={props.handleUpdateAutomationFiles}
        onClickEdit={() => {
          setEditing(true);
        }}
        locked={locked}
        disableEditButton={disableEditButton}
        review={props.review}
        automationFiles={props.automationFiles}
      />
    );
  }

  return (
    <>
      <AssayGroupContainer
        indentation={props.indentation}
        title={
          <AssayGroupHeader
            displayAnalysisTemplateIntegrationUI={displayAnalysisTemplateIntegrationUI}
            editButtons={editButtons}
            originType={props.assayRun.originType}
            titleText={props.assayRunSchemaInfo.versionedName || props.assayRunSchemaInfo.assayRunSchema.name}
          />
        }
        assayRunIdForValidationFetch={props.assayRun.id}
        showValidationSideBar={false}
      >
        {isAnalysisTemplateInaccessible && (
          <div className="u-spacing-bottom">
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="You do not have access to the analysis templates used by this run schema."
              supportingInfo="Analysis automation features are unavailable. Contact the template owner to request access."
            />
          </div>
        )}
        {displayAnalysisTemplateIntegrationUI ? (
          analysisTemplate == null || analysisIds == null ? (
            <CenteredHorizontalThrobber />
          ) : (
            <AnalysisTemplateIntegrationRunAssayGroupBody
              {...props}
              visibleRunFields={visibleRunFields}
              analysisTemplate={analysisTemplate}
              expandedState={integrationExpandedState}
              setExpandedState={setIntegrationExpandedState}
              fileVariableBindings={fileVariableBindings}
              setFileVariableBindings={setFileVariableBindings}
              analysisIds={analysisIds}
            />
          )
        ) : (
          <AssayGroupBody
            {...props}
            isExpanded={isExpanded}
            visibleRunFields={visibleRunFields}
            isEditing={isEditing}
            connectionSchema={connectionSchema}
            connectionStatus={connectionStatus}
            generatedInputFiles={generatedInputFiles}
            processedOutputFiles={processedOutputFiles}
            locked={locked}
            instrumentServiceDetails={instrumentServiceDetails}
            isAutomationRun={isAutomationRun}
          />
        )}
      </AssayGroupContainer>
      {!displayAnalysisTemplateIntegrationUI && (
        <div className={css(styles.collapserChevron)}>
          <CollapserChevron
            isExpanded={isExpanded}
            name={MODEL_NAMES.run}
            onToggleCollapsed={() => {
              setIsExpanded(!isExpanded);
            }}
          />
        </div>
      )}
    </>
  );
};

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onUpdateAssayRun: (
      assayRunId: string,
      cid: string,
      tags: Array<Tag>,
      connectionId: string | null,
      equipmentId: string | null,
      studyIds: Array<string>
    ) => dispatch(updateTagsForAssayRun(assayRunId, cid, tags, connectionId, equipmentId, studyIds)),
  };
}

export default connect(null, mapDispatchToProps)(AssayGroup);

const styles = StyleSheet.create({
  collapserChevron: {paddingTop: NOTE_PADDING, marginLeft: -5},
  analysisIntegrationUIHeader: {
    fontSize: 22,
    fontWeight: 400,
  },
  icon: {
    height: 12,
  },
});
